import { createContext, useContext, useMemo, useCallback } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { DEFAULT_BROKER_TAGS, DEFAULT_SETTINGS } from '../utils/constants';
import { computeFeatures } from '../utils/computeFeatures';
import { computeMyModel } from '../utils/computeMyModel';
import { computeOptimalModel } from '../utils/computeOptimalModel';
import { computeExitModel } from '../utils/computeExitModel';
import { computeSimulatorTrades } from '../utils/computeSimulatorTrades';
import { computeInstitutionalFlow } from '../utils/computeInstitutionalFlow';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [brokerData, setBrokerData] = useLocalStorage('bf4_brokerData', []);
  const [priceData, setPriceData] = useLocalStorage('bf4_priceData', []);
  const [brokerTags, setBrokerTags] = useLocalStorage('bf4_brokerTags', DEFAULT_BROKER_TAGS);
  const [settings, setSettings] = useLocalStorage('bf4_settings', DEFAULT_SETTINGS);
  const [realTrades, setRealTrades] = useLocalStorage('bf4_realTrades', []);
  const [watchlist, setWatchlist] = useLocalStorage('bf4_watchlist', []);
  const [transactionsData, setTransactionsData] = useLocalStorage('bf4_transactionsData', []);

  const { signals, filteredCount } = useMemo(() => {
    if (!brokerData.length) return { signals: [], filteredCount: 0 };
    // If no separate price data, try to derive from broker data value/volume
    const effectivePriceData = priceData.length > 0 ? priceData : derivePriceFromBroker(brokerData);
    if (!effectivePriceData.length) return { signals: [], filteredCount: 0 };

    const features = computeFeatures(brokerData, effectivePriceData, brokerTags);
    const maxPrice = settings.maxPrice || 5;
    const minLiq = settings.minLiquidity || 0;

    let filtered = 0;
    const sigs = features
      .filter(f => {
        if (f.price > maxPrice || f.liquidityScore < minLiq) { filtered++; return false; }
        return true;
      })
      .map(f => {
        const myModel = computeMyModel(f);
        const optModel = computeOptimalModel(f);
        const owned = realTrades.some(t => t.symbol === f.symbol && t.status === 'OPEN');
        const exitResult = computeExitModel(f, owned);
        const alignment = computeAlignment(myModel.myStage, optModel.optStage, myModel.myScore, optModel.optScore);
        return { ...f, ...myModel, ...optModel, ...exitResult, alignment };
      })
      .sort((a, b) => b.myScore - a.myScore);
    return { signals: sigs, filteredCount: filtered };
  }, [brokerData, priceData, brokerTags, settings, realTrades]);

  const simResult = useMemo(() => {
    return computeSimulatorTrades(signals, priceData, settings);
  }, [signals, priceData, settings]);

  const instFlow = useMemo(() => {
    if (!brokerData.length) return {};
    const effectivePriceData = priceData.length > 0 ? priceData : derivePriceFromBroker(brokerData);
    return computeInstitutionalFlow(brokerData, effectivePriceData, brokerTags);
  }, [brokerData, priceData, brokerTags]);

  const addRealTrade = useCallback((trade) => {
    const riskPerShare = trade.entryPrice - trade.stopPrice;
    const shares = trade.shares || (riskPerShare > 0
      ? Math.floor(((settings.simCapital || 500000) * (settings.riskPerTrade || 0.01)) / riskPerShare)
      : 0);
    setRealTrades(prev => [...prev, {
      ...trade,
      shares,
      id: Date.now().toString(),
      status: 'OPEN',
      openDate: new Date().toISOString(),
    }]);
  }, [setRealTrades, settings]);

  const closeRealTrade = useCallback((tradeId, exitPrice) => {
    setRealTrades(prev => prev.map(t =>
      t.id === tradeId
        ? {
          ...t,
          status: 'CLOSED',
          exitPrice: parseFloat(exitPrice),
          closeDate: new Date().toISOString(),
          pnl: (parseFloat(exitPrice) - t.entryPrice) * t.shares,
        }
        : t
    ));
  }, [setRealTrades]);

  const toggleWatchlist = useCallback((symbol) => {
    setWatchlist(prev =>
      prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol]
    );
  }, [setWatchlist]);

  const resetAllData = useCallback(() => {
    setBrokerData([]);
    setPriceData([]);
    setBrokerTags(DEFAULT_BROKER_TAGS);
    setSettings(DEFAULT_SETTINGS);
    setRealTrades([]);
    setWatchlist([]);
    setTransactionsData([]);
  }, [setBrokerData, setPriceData, setBrokerTags, setSettings, setRealTrades, setWatchlist, setTransactionsData]);

  const value = useMemo(() => ({
    brokerData, setBrokerData,
    priceData, setPriceData,
    brokerTags, setBrokerTags,
    settings, setSettings,
    realTrades, addRealTrade, closeRealTrade,
    watchlist, toggleWatchlist,
    transactionsData, setTransactionsData,
    signals, filteredCount,
    instFlow,
    simResult,
    resetAllData,
  }), [brokerData, setBrokerData, priceData, setPriceData, brokerTags, setBrokerTags,
    settings, setSettings, realTrades, addRealTrade, closeRealTrade,
    watchlist, toggleWatchlist, transactionsData, setTransactionsData,
    signals, filteredCount, instFlow, simResult, resetAllData]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}

function computeAlignment(myStage, optStage, myScore, optScore) {
  if (myStage === optStage) return 'ALIGN';
  if (Math.abs(myScore - optScore) > 2.5) return 'CONFLICT';
  return 'DIVERGE';
}

// Derive price data from broker data when no separate price CSV is available
function derivePriceFromBroker(brokerData) {
  const groups = {};
  brokerData.forEach(r => {
    const key = `${r.date}_${r.symbol}`;
    if (!groups[key]) groups[key] = { date: r.date, symbol: r.symbol, rows: [] };
    groups[key].rows.push(r);
  });

  return Object.values(groups).map(({ date, symbol, rows }) => {
    const totalValue = rows.reduce((s, r) => s + (r.totalValue || 0), 0);
    const totalVol = rows.reduce((s, r) => s + (r.buyVol || 0) + (r.sellVol || 0), 0);
    const avgPrice = totalVol > 0 ? totalValue / totalVol : 0;
    // Use buy/sell averages to estimate OHLC
    const allAves = rows.map(r => r.buyAve).concat(rows.map(r => r.sellAve)).filter(v => v > 0);
    const high = allAves.length > 0 ? Math.max(...allAves) : avgPrice;
    const low = allAves.length > 0 ? Math.min(...allAves) : avgPrice;

    return {
      date, symbol,
      open: avgPrice || low,
      high: high || avgPrice,
      low: low || avgPrice,
      close: avgPrice || high,
      volume: totalVol / 2, // divide by 2 since we count both sides
      value: totalValue / 2,
    };
  });
}
